import { useState, type ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';

import type { MotionPreference, QualityTier, ResourceDisplay } from '@aztec-realms/shared-types';
import {
  BottomSheet,
  CountdownTimer,
  DangerActionButton,
  IconButton,
  JadeGlassPanel,
  LoadingState,
  Modal,
  OfflineState,
  ParchmentPanel,
  PrimaryActionButton,
  ProgressMeter,
  RewardBundle,
  SecondaryActionButton,
  SideSheet,
  StatePanel,
  StonePanel,
  Toast,
} from '@aztec-realms/ui';

import { usePresentationSettings } from './presentation-settings';
import { AvatarSettings } from './AvatarSettings';
import { PrivacySettings } from './PrivacySettings';
import { PushSettings } from './PushSettings';
import { avatarCatalogVisualFixture } from './data/avatar-fixture';
import { invalidateBrowserSession } from './data/browser-api';
import { terminateBrowserSession } from './data/session-teardown';
import { visualFixtureName } from './data/vertical-slice';
import { useNetworkStatus } from './network-status';
import { useVisualReady } from './visual-ready';
import { GuideEntry } from './OnboardingGuide';

interface RealmScreenProps {
  eyebrow: string;
  title: string;
  description: string;
  action: string;
  motif: 'settlement' | 'map' | 'glyph' | 'codex' | 'city';
  children?: ReactNode;
}

function RealmScreen({ eyebrow, title, description, action, motif, children }: RealmScreenProps) {
  return (
    <section className="realm-screen" data-motif={motif}>
      <div aria-hidden="true" className="realm-screen__sky" />
      <div aria-hidden="true" className="realm-screen__water" />
      <div aria-hidden="true" className="realm-screen__terraces">
        <span />
        <span />
        <span />
      </div>
      <div className="realm-screen__content">
        <span className="realm-screen__eyebrow">{eyebrow}</span>
        <h1>{title}</h1>
        <p>{description}</p>
        <PrimaryActionButton>{action}</PrimaryActionButton>
        {children}
      </div>
    </section>
  );
}

export function HomeScreen() {
  return (
    <RealmScreen
      action="Begin restoration"
      description="Waterways and gardens wait for the first structure that will make this settlement yours."
      eyebrow="A humble beginning"
      motif="settlement"
      title="Your settlement remembers every journey"
    >
      <div className="realm-screen__facts" aria-label="Settlement status">
        <span>3 sites ready</span>
        <span>Calm waters</span>
        <span>First light</span>
      </div>
    </RealmScreen>
  );
}

export function ExploreScreen() {
  return (
    <RealmScreen
      action="Review nearby discoveries"
      description="Real paths will reveal a living codex while your exact journey stays private."
      eyebrow="Explore"
      motif="map"
      title="Wonder begins just beyond the known path"
    />
  );
}

export function DecodeScreen() {
  return (
    <RealmScreen
      action="Open today’s fragment"
      description="Restore a radial glyph mechanism, trace its energy, and return its knowledge to the realm."
      eyebrow="Decode"
      motif="glyph"
      title="Turn stone into understanding"
    />
  );
}

export function CodexScreen() {
  return (
    <RealmScreen
      action="Browse known glyphs"
      description="Every discovery becomes part of a maintained, living archive of regions, artifacts and stories."
      eyebrow="Codex"
      motif="codex"
      title="Knowledge endures"
    />
  );
}

export function TribeScreen() {
  return (
    <RealmScreen
      action="Discover civic projects"
      description="Build something larger together while your solo path remains complete and prestigious."
      eyebrow="Tribe"
      motif="city"
      title="A brighter realm, built together"
    />
  );
}

const qualityOptions: readonly { value: QualityTier; title: string; description: string }[] = [
  { value: 'auto', title: 'Automatic', description: 'Choose detail for this device.' },
  { value: 'low', title: 'Low', description: 'Calmer effects and lighter assets.' },
  { value: 'medium', title: 'Medium', description: 'Balanced motion and detail.' },
  { value: 'high', title: 'High', description: 'Richer ambience on capable devices.' },
];

const motionOptions: readonly { value: MotionPreference; title: string }[] = [
  { value: 'system', title: 'Match device' },
  { value: 'reduce', title: 'Reduce motion' },
  { value: 'full', title: 'Full motion' },
];

export function SettingsScreen({
  interactionLocked = false,
  ownerId,
}: {
  interactionLocked?: boolean;
  ownerId?: string;
}) {
  const settings = usePresentationSettings();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const online = useNetworkStatus();
  const [terminatingSession, setTerminatingSession] = useState(false);
  const visualState =
    visualFixtureName() === 'reduced-motion' ? 'settings-reduced-motion' : undefined;
  useVisualReady(visualState, true);

  return (
    <section
      className="settings-screen"
      data-visual-ready={visualState ? 'true' : undefined}
      data-visual-state={visualState}
    >
      <header className="settings-hero">
        <div>
          <span className="realm-screen__eyebrow">Profile &amp; settings</span>
          <h1>Your wayfinder account</h1>
          <p>
            Manage your identity, presentation preferences and private session in one place. Display
            choices never change rewards, puzzle rules or discovery eligibility.
          </p>
        </div>
        <PrimaryActionButton onClick={() => navigate('/home')}>
          Return to the settlement
        </PrimaryActionButton>
      </header>

      <StonePanel className="settings-card settings-card--session">
        <span className="realm-screen__eyebrow">Private session</span>
        <h2>{online ? 'Signed in on this device' : 'Offline realm view'}</h2>
        <p>
          {online
            ? 'Sign out to revoke the server session and remove this player’s cached realm from the device.'
            : 'Remove cached player data from this device now. Server revocation requires a connection.'}
        </p>
        <DangerActionButton
          disabled={terminatingSession}
          loading={terminatingSession}
          onClick={() => {
            setTerminatingSession(true);
            invalidateBrowserSession({
              code: 'signed_out',
              message: 'Player signed out',
            });
            void terminateBrowserSession(queryClient, { online })
              .then(() => {
                navigate('/', { replace: true });
              })
              .catch(() => {
                invalidateBrowserSession({
                  code: 'session_cleanup_failed',
                  message: 'Local session ended; server revocation could not be confirmed',
                });
                navigate('/', { replace: true });
              });
          }}
        >
          {online ? 'Sign out and clear this device' : 'Clear private offline data'}
        </DangerActionButton>
      </StonePanel>

      <StonePanel className="settings-card settings-card--motion">
        <fieldset className="settings-fieldset">
          <legend>Motion</legend>
          {motionOptions.map((option) => (
            <label className="settings-option" key={option.value}>
              <input
                checked={settings.motionPreference === option.value}
                name="motion"
                onChange={() => settings.setMotionPreference(option.value)}
                type="radio"
              />
              <span>
                <strong>{option.title}</strong>
              </span>
            </label>
          ))}
        </fieldset>
      </StonePanel>

      <StonePanel className="settings-card settings-card--quality">
        <fieldset className="settings-fieldset settings-fieldset--quality">
          <legend>Visual quality</legend>
          {qualityOptions.map((option) => (
            <label className="settings-option" key={option.value}>
              <input
                checked={settings.qualityTier === option.value}
                name="quality"
                onChange={() => settings.setQualityTier(option.value)}
                type="radio"
              />
              <span>
                <strong>{option.title}</strong>
                <small>{option.description}</small>
              </span>
            </label>
          ))}
        </fieldset>
      </StonePanel>

      <StonePanel className="settings-card settings-card--readability">
        <fieldset className="settings-fieldset">
          <legend>Readability</legend>
          <label className="settings-option">
            <input
              checked={settings.interfaceScale === 'large'}
              onChange={(event) =>
                settings.setInterfaceScale(event.target.checked ? 'large' : 'default')
              }
              type="checkbox"
            />
            <span>
              <strong>Larger interface text</strong>
              <small>Increase labels without changing the game view.</small>
            </span>
          </label>
          <label className="settings-option">
            <input
              checked={settings.highContrast}
              onChange={(event) => settings.setHighContrast(event.target.checked)}
              type="checkbox"
            />
            <span>
              <strong>High contrast materials</strong>
              <small>Strengthen text, borders and active energy paths.</small>
            </span>
          </label>
        </fieldset>
      </StonePanel>

      <StonePanel className="settings-card settings-card--identity">
        <AvatarSettings
          fixtureCatalog={visualState ? avatarCatalogVisualFixture : undefined}
          online={online}
        />
      </StonePanel>

      <PrivacySettings online={online && !visualState} interactionLocked={interactionLocked} />
      <PushSettings
        ownerId={visualState ? undefined : ownerId}
        online={online}
        interactionLocked={interactionLocked}
      />
      <GuideEntry returnTo="/settings" />

      <StonePanel className="settings-card settings-card--market">
        <span className="realm-screen__eyebrow">Optional support</span>
        <h2>Lakeside market</h2>
        <p>
          Review cosmetic and bounded convenience offers. Purchases never buy Decode mastery,
          exploration rewards or Tribe rank.
        </p>
        <PrimaryActionButton onClick={() => navigate('/shop')}>
          Review fair-play offers
        </PrimaryActionButton>
      </StonePanel>
    </section>
  );
}

const previewRewards: readonly ResourceDisplay[] = [
  { code: 'maize', label: 'Maize', amount: 400 },
  { code: 'jade', label: 'Jade', amount: 12 },
  { code: 'knowledge', label: 'Knowledge', amount: 1 },
];

export function VisualKitScreen() {
  const [modalOpen, setModalOpen] = useState(false);

  return (
    <section className="visual-kit">
      <header>
        <span className="realm-screen__eyebrow">Development surface</span>
        <h1>Visual component kit</h1>
        <p>Semantic materials and interaction states shown against the same realm tokens.</p>
      </header>

      <section className="visual-kit__section" aria-labelledby="kit-actions">
        <h2 id="kit-actions">Actions</h2>
        <div className="visual-kit__row">
          <PrimaryActionButton>Primary action</PrimaryActionButton>
          <SecondaryActionButton>Secondary action</SecondaryActionButton>
          <DangerActionButton>Danger action</DangerActionButton>
          <PrimaryActionButton disabled>Disabled action</PrimaryActionButton>
          <PrimaryActionButton loading>Loading action</PrimaryActionButton>
          <IconButton label="Inspect glyph">◎</IconButton>
        </div>
      </section>

      <section className="visual-kit__section" aria-labelledby="kit-panels">
        <h2 id="kit-panels">Materials</h2>
        <div className="visual-kit__grid">
          <StonePanel>
            <h3>Carved stone</h3>
            <p>Primary game panel with restrained ornament.</p>
          </StonePanel>
          <JadeGlassPanel>
            <h3>Jade glass</h3>
            <p>Selected or magical state over a living scene.</p>
          </JadeGlassPanel>
          <ParchmentPanel>
            <h3>Living parchment</h3>
            <p>Readable lore and collection detail.</p>
          </ParchmentPanel>
        </div>
      </section>

      <section className="visual-kit__section" aria-labelledby="kit-economy">
        <h2 id="kit-economy">Progress and rewards</h2>
        <div className="visual-kit__grid">
          <StonePanel>
            <RewardBundle resources={previewRewards} />
          </StonePanel>
          <StonePanel>
            <ProgressMeter label="Civic restoration" value={68} />
            <CountdownTimer label="Remaining" remaining="3h 20m" />
          </StonePanel>
          <Toast title="Codex updated" tone="success">
            The Sun family gained one discovery.
          </Toast>
        </div>
      </section>

      <section className="visual-kit__section" aria-labelledby="kit-states">
        <h2 id="kit-states">Surface states</h2>
        <div className="visual-kit__grid">
          <LoadingState />
          <StatePanel kind="empty" title="No discoveries yet">
            Start a safe journey when you are ready.
          </StatePanel>
          <StatePanel
            actionLabel="Try again"
            kind="error"
            onAction={() => undefined}
            title="The path faded"
          >
            We could not refresh this view.
          </StatePanel>
          <OfflineState />
        </div>
      </section>

      <section className="visual-kit__section" aria-labelledby="kit-overlays">
        <h2 id="kit-overlays">Overlays</h2>
        <div className="visual-kit__grid">
          <BottomSheet title="Selected discovery">
            <p>A fragment rests nearby.</p>
          </BottomSheet>
          <SideSheet title="Building details">
            <p>Upgrade choices will appear here.</p>
          </SideSheet>
          <StonePanel>
            <PrimaryActionButton onClick={() => setModalOpen(true)}>Open modal</PrimaryActionButton>
          </StonePanel>
        </div>
      </section>

      <Modal onClose={() => setModalOpen(false)} open={modalOpen} title="Restore this structure?">
        <p>The server will verify resources before any lasting change is made.</p>
        <div className="visual-kit__row">
          <SecondaryActionButton onClick={() => setModalOpen(false)}>Cancel</SecondaryActionButton>
          <PrimaryActionButton onClick={() => setModalOpen(false)}>Continue</PrimaryActionButton>
        </div>
      </Modal>
    </section>
  );
}

export function NotFoundScreen() {
  const navigate = useNavigate();
  const visualState = visualFixtureName() === 'not-found' ? 'not-found' : undefined;
  useVisualReady(visualState, true);

  return (
    <section
      className="not-found-screen"
      data-visual-ready={visualState ? 'true' : undefined}
      data-visual-state={visualState}
    >
      <div aria-hidden="true" className="not-found-screen__map-mark" />
      <div className="not-found-screen__content">
        <span className="realm-screen__eyebrow">Beyond the known map</span>
        <StatePanel
          actionLabel="Return home"
          kind="empty"
          onAction={() => navigate('/home')}
          title="This path is not mapped"
        >
          The requested part of the realm could not be found. Your settlement remains safe at the
          last known waypoint.
        </StatePanel>
      </div>
    </section>
  );
}
